// Filter Drawer - AJAX Implementation (No Page Reload)

class FilterDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.drawer = this;
    this.panel = this.querySelector('.filter-drawer__panel');
    this.overlay = this.querySelector('.filter-drawer__overlay');
    this.closeButton = this.querySelector('.filter-drawer__close');
    this.form = this.querySelector('.filter-drawer__form');
    this.clearButton = this.querySelector('.filter-drawer__button--clear');
    this.applyButton = this.querySelector('.filter-drawer__button--apply');
    this.sectionId = this.dataset.sectionId;
    this.isLoading = false;

    this.setupEventListeners();
    this.setupAccordions();
  }

  // Null-safe access to the Shopify Inbox chat widget's z-index. The
  // chat element may be absent (ad-blocker, slow CDN, app disabled per-
  // page, or Shopify Inbox markup changes); without this guard every
  // call site below would TypeError on null.style and tear down the
  // handler invoking it. On the fetchFilteredResults() path the catch
  // then falls back to window.location.href = url — a full page reload
  // with a brief blank during nav. Pass null to clear the override,
  // a string value to set it.
  setShopifyChatZIndex(value) {
    const chat = document.getElementById('shopify-chat');
    if (!chat) return;
    if (value === null) {
      chat.style.removeProperty('z-index');
    } else {
      chat.style.zIndex = value;
    }
  }

  setupEventListeners() {
    // Prevent ALL form submissions
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }, true);

      this.form.onsubmit = function() {
        return false;
      };
    }

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
        this.setShopifyChatZIndex(null);
      });
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
        this.setShopifyChatZIndex(null);
      });
    }

    // Clear filters
    if (this.clearButton) {
      this.clearButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.clearFilters();
        return false;
      });
    }

    // Apply filters
    if (this.applyButton) {
      this.applyButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.applyFilters();
        return false;
      }, true);

      this.applyButton.onclick = (e) => {
        e.preventDefault();
        this.applyFilters();
        return false;
      };
    }

    // Escape key to close
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.classList.contains('active')) {
        this.close();
        this.setShopifyChatZIndex(null);
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  setupAccordions() {
    const accordions = this.querySelectorAll('.filter-drawer__group');
    accordions.forEach(accordion => {
      const summary = accordion.querySelector('.filter-drawer__group-summary');
      if (summary) {
        summary.addEventListener('click', (e) => {
          // Let native details/summary behavior work
        });
      }
    });
  }

  open() {
    this.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.setShopifyChatZIndex('-1');
    setTimeout(() => {
      this.trapFocus();
    }, 100);
  }

  close() {
    this.classList.remove('active');
    document.body.style.overflow = '';
    this.setShopifyChatZIndex(null);

    const trigger = document.querySelector('.filter-drawer-trigger');
    if (trigger) {
      trigger.focus();
    }
  }

  clearFilters() {
    if (this.isLoading) return;

    const checkboxes = this.form.querySelectorAll('input[type="checkbox"]:checked');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    const priceInputs = this.form.querySelectorAll('.filter-drawer__price-input');
    priceInputs.forEach(input => {
      input.value = '';
    });

    this.applyFilters();
  }

  applyFilters() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();

    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams(window.location.search);

    // Only clear keys this drawer's form owns. Anything else in the URL
    // (e.g. quick-filter pill params like filter.p.tag=Jeans that aren't
    // represented as a form input) is preserved.
    const ownedKeys = new Set();
    this.form.querySelectorAll('[name]').forEach((input) => ownedKeys.add(input.name));
    ownedKeys.add('sort_by');
    ownedKeys.add('page');
    ownedKeys.forEach((key) => searchParams.delete(key));

    // Add new filter params
    for (let [key, value] of formData.entries()) {
      if (value) {
        searchParams.append(key, value);
      }
    }

    // Preserve sort order from drawer
    const sortSelect = document.querySelector('#SortBy-drawer');
    if (sortSelect && sortSelect.value) {
      searchParams.set('sort_by', sortSelect.value);
    }

    // Build URL
    const queryString = searchParams.toString();
    const url = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

    // Update browser URL without reload. Store searchParams (matching the shape
    // FacetFiltersForm pushes) so Back/Forward to this entry re-renders the
    // correct filtered grid, and keep FacetFiltersForm's dedupe marker in sync
    // so its popstate handler won't skip the re-render — the drawer renders the
    // grid itself here, bypassing FacetFiltersForm.renderPage.
    window.history.pushState({ searchParams: queryString, path: url }, '', url);
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.searchParamsPrev = queryString;
    }

    // Fetch filtered results
    this.fetchFilteredResults(url);
  }

  async fetchFilteredResults(url) {
    try {
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      this.updateProductGrid(doc);
      this.updateFilterCounts(doc);
      this.updateActiveFilterCount();

      // Adopt the server-rendered count badge (it excludes pills-only params like
      // style_category and counts the Sale tag). Without this the drawer-apply path
      // keeps the checkbox-derived estimate above, which lags the real count — e.g.
      // Jeans + Sale would read (1) instead of (2). syncFromUrl already does this.
      const newTrigger = doc.querySelector('.filter-drawer-trigger');
      const currentTrigger = document.querySelector('.filter-drawer-trigger');
      if (newTrigger && currentTrigger) {
        currentTrigger.innerHTML = newTrigger.innerHTML;
      }

      this.close();
      this.setShopifyChatZIndex(null);
      // this.scrollToProducts();
    } catch (error) {
      console.error('Error fetching filtered results:', error);
      window.location.href = url;
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  updateProductGrid(doc) {
    const newProductGrid = doc.querySelector('#product-grid, .product-grid, [id*="product-grid"]');
    const currentProductGrid = document.querySelector('#product-grid, .product-grid, [id*="product-grid"]');

    if (newProductGrid && currentProductGrid) {
      currentProductGrid.style.opacity = '0.5';
      currentProductGrid.style.transition = 'opacity 0.3s ease';

      setTimeout(() => {
        currentProductGrid.innerHTML = newProductGrid.innerHTML;
        currentProductGrid.style.opacity = '1';
        this.reinitializeProductCards();
      }, 300);
    }

    const newPagination = doc.querySelector('.pagination, [class*="pagination"]');
    const currentPagination = document.querySelector('.pagination, [class*="pagination"]');

    if (newPagination && currentPagination) {
      currentPagination.innerHTML = newPagination.innerHTML;
    }

    const newProductCount = doc.querySelector('.product-count, [class*="product-count"]');
    const currentProductCount = document.querySelector('.product-count, [class*="product-count"]');

    if (newProductCount && currentProductCount) {
      currentProductCount.innerHTML = newProductCount.innerHTML;
    }
  }

  updateFilterCounts(doc) {
    // Match groups by their data-filter-param key, not by DOM position. Accordions
    // appear/disappear between views (e.g. Waist/Length have no values under
    // Accessories) and the "Sale" group renders last — both shift indices, so
    // positional pairing would sync counts onto the wrong group.
    const currentGroupsByParam = new Map();
    this.querySelectorAll('.filter-drawer__group').forEach((group) => {
      const key = group.dataset.filterParam;
      if (key) currentGroupsByParam.set(key, group);
    });

    const newFilterGroups = doc.querySelectorAll('.filter-drawer__group');

    newFilterGroups.forEach((newGroup) => {
      const currentGroup = currentGroupsByParam.get(newGroup.dataset.filterParam);
      if (!currentGroup) return;

      const newCheckboxes = newGroup.querySelectorAll('.filter-drawer__checkbox-label');
      const currentCheckboxes = currentGroup.querySelectorAll('.filter-drawer__checkbox-label');

      newCheckboxes.forEach((newCheckbox, checkboxIndex) => {
        const currentCheckbox = currentCheckboxes[checkboxIndex];
        if (!currentCheckbox) return;

        const newCount = newCheckbox.querySelector('.filter-drawer__checkbox-count');
        const currentCount = currentCheckbox.querySelector('.filter-drawer__checkbox-count');

        if (newCount && currentCount) {
          currentCount.textContent = newCount.textContent;
        }

        const newInput = newCheckbox.querySelector('input');
        const currentInput = currentCheckbox.querySelector('input');

        if (newInput && currentInput) {
          currentInput.checked = newInput.checked;
          currentCheckbox.classList.toggle('active', newInput.checked);

          if (newInput.disabled) {
            currentCheckbox.classList.add('disabled');
            currentInput.disabled = true;
          } else {
            currentCheckbox.classList.remove('disabled');
            currentInput.disabled = false;
          }
        }
      });
    });

    // Sync price range inputs
    const newPriceInputs = doc.querySelectorAll('.filter-drawer__price-input');
    const currentPriceInputs = this.querySelectorAll('.filter-drawer__price-input');
    newPriceInputs.forEach((newInput, i) => {
      const currentInput = currentPriceInputs[i];
      if (currentInput) currentInput.value = newInput.value || '';
    });

    // Sync sort dropdown to current URL state
    const newSort = doc.querySelector('#SortBy-drawer');
    const currentSort = document.querySelector('#SortBy-drawer');
    if (newSort && currentSort && newSort.value !== currentSort.value) {
      currentSort.value = newSort.value;
      const selectText = document.querySelector('[data-select-text]');
      if (selectText) {
        selectText.textContent = currentSort.options[currentSort.selectedIndex].text;
      }
    }
  }

  // Refresh drawer state (checkboxes, prices, sort, trigger badge) from a URL
  // without touching the product grid. NOTE: the drawer body (#FilterDrawer with
  // its .filter-drawer__group markup) is rendered in the LAYOUT, not inside the
  // grid section FacetFiltersForm fetches (`?section_id=`), so its markup is only
  // available from a full-page fetch — we cannot reuse facets' section doc or a
  // per-section fetch here. Used when an external actor (quick-filter pill, sort,
  // back/forward) updated the URL and already re-rendered the grid.
  async syncFromUrl(url) {
    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.updateFilterCounts(doc);
      this.updateActiveFilterCount();

      const newTrigger = doc.querySelector('.filter-drawer-trigger');
      const currentTrigger = document.querySelector('.filter-drawer-trigger');
      if (newTrigger && currentTrigger) {
        currentTrigger.innerHTML = newTrigger.innerHTML;
      }
    } catch (error) {
      console.error('Error syncing filter drawer:', error);
    }
  }

  // Refresh drawer state from the grid-section document FacetFiltersForm ALREADY
  // fetched (dispatched via `facets:filters-updated`) — no second full-page
  // round-trip. The section's `.facets__label` elements carry the same filter
  // values as the drawer's checkboxes, matched by input name+value (robust to
  // ordering and to filters appearing/disappearing between category views). Sort
  // + price are read from the URL because those drawer controls aren't in the
  // grid section. Drawer checkboxes with no matching section facet (e.g. the
  // hardcoded Sale tag group when no tag facet is configured) are left untouched,
  // never zeroed.
  syncFromFacets(doc) {
    if (doc) {
      const facetState = new Map();
      doc.querySelectorAll('.facets__label').forEach((label) => {
        const input = label.querySelector('input[name]');
        if (!input || !input.name || input.value === '') return;
        let count = parseInt(label.dataset.count, 10);
        if (Number.isNaN(count)) {
          const match = label.textContent.match(/\((\d+)\)/);
          count = match ? parseInt(match[1], 10) : null;
        }
        facetState.set(`${input.name}=${input.value}`, {
          count,
          checked: input.checked,
          disabled: input.disabled || label.classList.contains('disabled'),
        });
      });

      if (facetState.size) {
        this.querySelectorAll('.filter-drawer__checkbox-label').forEach((labelEl) => {
          const input = labelEl.querySelector('input[name]');
          if (!input || !input.name) return;
          const state = facetState.get(`${input.name}=${input.value}`);
          if (!state) return;

          const countEl = labelEl.querySelector('.filter-drawer__checkbox-count');
          if (countEl && state.count !== null) countEl.textContent = `(${state.count})`;

          input.checked = state.checked;
          labelEl.classList.toggle('active', state.checked);
          input.disabled = state.disabled;
          labelEl.classList.toggle('disabled', state.disabled);
        });
      }
    }

    this.syncControlsFromUrl();
    this.updateActiveFilterCount();
  }

  // Sync the drawer's sort dropdown + price inputs from the current URL — these
  // controls live in the drawer (layout), not the fetched grid section.
  syncControlsFromUrl() {
    const params = new URLSearchParams(window.location.search);

    const currentSort = document.querySelector('#SortBy-drawer');
    const sortValue = params.get('sort_by');
    if (currentSort && sortValue && currentSort.value !== sortValue) {
      currentSort.value = sortValue;
      const selectText = document.querySelector('[data-select-text]');
      if (selectText && currentSort.options[currentSort.selectedIndex]) {
        selectText.textContent = currentSort.options[currentSort.selectedIndex].text;
      }
    }

    this.querySelectorAll('.filter-drawer__price-input').forEach((input) => {
      if (input.name) input.value = params.get(input.name) || '';
    });
  }

  updateActiveFilterCount() {
    const checkedFilters = this.form.querySelectorAll('input[type="checkbox"]:checked');
    const priceInputs = this.form.querySelectorAll('.filter-drawer__price-input');
    let priceFilterActive = false;

    priceInputs.forEach(input => {
      if (input.value) priceFilterActive = true;
    });

    const totalActive = checkedFilters.length + (priceFilterActive ? 1 : 0);
    const trigger = document.querySelector('.filter-drawer-trigger');
    if (!trigger) return;

    let countBadge = trigger.querySelector('.filter-drawer-trigger__count');

    if (totalActive > 0) {
      if (!countBadge) {
        countBadge = document.createElement('span');
        countBadge.className = 'filter-drawer-trigger__count';
        trigger.appendChild(countBadge);
      }
      countBadge.textContent = `(${totalActive})`;
    } else {
      if (countBadge) {
        countBadge.remove();
      }
    }
  }

  reinitializeProductCards() {
    if (typeof window.initializeQuickAdd === 'function') {
      window.initializeQuickAdd();
    }

    document.dispatchEvent(new CustomEvent('filterDrawer:productsUpdated'));
  }

  scrollToProducts() {
    const productGrid = document.querySelector('#product-grid, .product-grid, [id*="product-grid"]');
    if (productGrid) {
      const offset = 100;
      const elementPosition = productGrid.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  showLoading() {
    if (this.applyButton) {
      this.applyButton.disabled = true;
      this.applyButton.style.opacity = '0.6';
      this.applyButton.textContent = 'Applying...';
    }

    const productGridContainer = document.querySelector('#ProductGridContainer, [id*="ProductGridContainer"]');
    if (productGridContainer) {
      let loadingOverlay = productGridContainer.querySelector('.loading-overlay');
      if (!loadingOverlay) {
        loadingOverlay = document.createElement('div');
        loadingOverlay.className = 'loading-overlay';
        loadingOverlay.innerHTML = '<div class="loading-spinner"></div>';
        productGridContainer.style.position = 'relative';
        productGridContainer.appendChild(loadingOverlay);
      }
      loadingOverlay.style.display = 'flex';
      loadingOverlay.style.opacity = '1';
    }
  }

  hideLoading() {
    if (this.applyButton) {
      this.applyButton.disabled = false;
      this.applyButton.style.opacity = '1';

      const applyText = this.applyButton.dataset.originalText || 'Apply';
      this.applyButton.textContent = applyText;
    }

    const loadingOverlay = document.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.style.opacity = '0';
      setTimeout(() => {
        loadingOverlay.style.display = 'none';
      }, 300);
    }
  }

  trapFocus() {
    if (!this.panel) return;

    const focusableElements = this.panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (firstElement) {
      firstElement.focus();
    }

    this.tabHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (!this.classList.contains('active')) return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.panel.addEventListener('keydown', this.tabHandler);
  }

  disconnectedCallback() {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
    }
    if (this.tabHandler && this.panel) {
      this.panel.removeEventListener('keydown', this.tabHandler);
    }
  }
}

// Define custom element
if (!customElements.get('filter-drawer')) {
  customElements.define('filter-drawer', FilterDrawer);
}

// Track if already initialized
let filterDrawerInitialized = false;

// Initialize filter drawer
function initFilterDrawer(force = false) {
  if (filterDrawerInitialized && !force) return;

  const drawer = document.querySelector('filter-drawer');

  if (!drawer) return;

  filterDrawerInitialized = true;

  // The Filter trigger button is handled by a delegated document-level click
  // listener (see bindFilterDrawerTrigger below), so it keeps working even after
  // an AJAX filter apply or a theme-editor section reload replaces the trigger.
  // The previous code bound the listener directly to the element, so any
  // re-render that swapped the trigger left a dead button.

  // Sort dropdown inside drawer - auto-apply on change
  const sortSelect = document.querySelector('#SortBy-drawer');
  if (sortSelect) {
    // Update custom select display text
    const updateSelectDisplay = () => {
      const selectText = document.querySelector('[data-select-text]');
      if (selectText) {
        const selectedOption = sortSelect.options[sortSelect.selectedIndex];
        selectText.textContent = selectedOption.text;
      }
    };

    // Initialize display text
    updateSelectDisplay();

    sortSelect.addEventListener('change', (e) => {
      // Update display text
      updateSelectDisplay();

      // Automatically apply filters when sort changes
      if (drawer && drawer.applyFilters) {
        drawer.applyFilters();
      }
    });
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initFilterDrawer());
} else {
  initFilterDrawer();
}

// Backup initialization (only if not already initialized)
setTimeout(() => {
  if (!filterDrawerInitialized) {
    initFilterDrawer();
  }
}, 500);

// Allow re-binding when drawer markup is replaced dynamically
window.refreshFilterDrawerTriggers = function refreshFilterDrawerTriggers() {
  filterDrawerInitialized = false;
  initFilterDrawer(true);
};

// Delegated Filter-button handler — bound once for the page lifetime. Because it
// lives on `document` (not the trigger element), it keeps working after any
// re-render that swaps out the .filter-drawer-trigger: an AJAX filter apply,
// quick-filter pills, or a theme-editor section reload. The drawer is re-queried
// per click so a re-rendered drawer element is handled too.
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.filter-drawer-trigger');
  if (!trigger) return;
  e.preventDefault();
  const drawer = document.querySelector('filter-drawer');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  }
});

// Handle browser back/forward buttons.
//
// On v2 collection pages FacetFiltersForm owns the product-grid re-render on
// history navigation (it replaces #ProductGridContainer from a ?section_id=
// fetch). The drawer must NOT also fetch/replace the grid here — two engines
// writing the overlapping #product-grid node race and can paint stale/unfiltered
// results. So when FacetFiltersForm is present we only sync the drawer's own UI
// (checkboxes, counts, trigger badge) from the restored URL; on a drawer-only
// page the drawer remains the sole grid engine and re-renders as before.
window.addEventListener('popstate', (e) => {
  const drawer = document.querySelector('filter-drawer');
  if (!drawer) {
    window.location.reload();
    return;
  }
  // On v2 pages FacetFiltersForm re-renders the grid on history nav and dispatches
  // `facets:filters-updated`, which refreshes the drawer (see listener below) — so
  // there's nothing to do here. On a drawer-only page the drawer is the sole grid
  // engine and must re-fetch.
  if (!document.querySelector('facet-filters-form')) {
    drawer.fetchFilteredResults(window.location.href);
  }
});

// External URL changes (quick-filter pill clicks, sort, back/forward) — refresh
// the drawer's checkbox counts by REUSING the grid section FacetFiltersForm just
// fetched, rather than a second full-page round-trip. facets.js dispatches this
// after each grid re-render with the parsed section document.
document.addEventListener('facets:filters-updated', (event) => {
  const drawer = document.querySelector('filter-drawer');
  if (drawer && typeof drawer.syncFromFacets === 'function') {
    drawer.syncFromFacets(event.detail && event.detail.doc);
  }
});

// Handle active filter tags removal
document.addEventListener('click', (e) => {
  if (e.target.closest('.active-filter-tag')) {
    e.preventDefault();
    const tag = e.target.closest('.active-filter-tag');
    const filterParam = tag.dataset.filterParam;
    const filterValue = tag.dataset.filterValue;

    if (filterParam && filterValue) {
      const drawer = document.querySelector('filter-drawer');

      if (drawer) {
        const checkbox = drawer.querySelector(`input[name="${filterParam}"][value="${filterValue}"]`);
        if (checkbox) {
          checkbox.checked = false;
        }
        drawer.applyFilters();
      } else {
        const url = new URL(window.location.href);
        const params = url.searchParams.getAll(filterParam);

        url.searchParams.delete(filterParam);
        params.forEach(param => {
          if (param !== filterValue) {
            url.searchParams.append(filterParam, param);
          }
        });

        window.location.href = url.toString();
      }
    }
  }
});
